import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import { 
  MessageSquare, Users, Clock, User, Plus, 
  Eye, MessageCircle, ThumbsUp, Award, Flag, 
  Reply, Edit, Trash, Calendar
} from 'lucide-react';
import './Tavern.css';

// Mock data - will be replaced with actual data from the API
const MOCK_CATEGORIES = [
  {
    id: '1',
    name: 'Announcements',
    description: 'Official announcements from the ChoiceRealms team',
    threadCount: 5,
    postCount: 12,
    recentThreads: [
      { id: '101', title: 'Welcome to ChoiceRealms', lastActivity: '2023-09-15T14:30:00Z', author: 'GameMaster' },
      { id: '102', title: 'Latest Updates and Upcoming Features', lastActivity: '2023-09-10T10:15:00Z', author: 'GameMaster' }
    ]
  },
  {
    id: '2',
    name: 'General Discussion',
    description: 'Talk about anything related to ChoiceRealms',
    threadCount: 24,
    postCount: 189,
    recentThreads: [
      { id: '201', title: 'What class is everyone playing?', lastActivity: '2023-09-14T09:45:00Z', author: 'AdventurerJohn' },
      { id: '202', title: 'Looking for party members', lastActivity: '2023-09-13T22:30:00Z', author: 'DungeonCrawler' }
    ]
  },
  {
    id: '3',
    name: 'Character Builds',
    description: 'Share and discuss character builds and strategies',
    threadCount: 18,
    postCount: 142,
    recentThreads: [
      { id: '301', title: 'Ultimate Wizard Guide', lastActivity: '2023-09-12T17:20:00Z', author: 'SpellcasterSupreme' },
      { id: '302', title: 'Tank Builds for Wintermere', lastActivity: '2023-09-11T13:10:00Z', author: 'ShieldWall' }
    ]
  },
  {
    id: '4',
    name: 'Game Modules',
    description: 'Discuss official and community game modules',
    threadCount: 15,
    postCount: 87,
    recentThreads: [
      { id: '401', title: 'Wintermere Feedback Thread', lastActivity: '2023-09-13T11:05:00Z', author: 'IcyExplorer' },
      { id: '402', title: 'Dark Forest Module Guide', lastActivity: '2023-09-09T19:40:00Z', author: 'ForestWanderer' }
    ]
  },
  {
    id: '5',
    name: 'Technical Support',
    description: 'Get help with technical issues',
    threadCount: 8,
    postCount: 36,
    recentThreads: [
      { id: '501', title: 'Character not loading properly', lastActivity: '2023-09-14T16:55:00Z', author: 'TroubledPlayer' },
      { id: '502', title: 'Login Issues After Update', lastActivity: '2023-09-10T08:30:00Z', author: 'HelpMePlease' }
    ]
  }
];

const MOCK_THREADS = {
  '1': [
    {
      id: '101',
      title: 'Welcome to ChoiceRealms',
      content: 'Welcome to the official forums for ChoiceRealms! This is where you can discuss all aspects of the game, share your experiences, and connect with other players.',
      author: 'GameMaster',
      createdAt: '2023-09-01T10:00:00Z',
      lastActivity: '2023-09-15T14:30:00Z',
      lastPostAuthor: 'GameMaster',
      viewCount: 352,
      replyCount: 7,
      isPinned: true,
      isLocked: false
    },
    {
      id: '102',
      title: 'Latest Updates and Upcoming Features',
      content: 'Check out our latest updates and a sneak peek at upcoming features!',
      author: 'GameMaster',
      createdAt: '2023-09-05T14:00:00Z',
      lastActivity: '2023-09-10T10:15:00Z',
      lastPostAuthor: 'ExcitedPlayer',
      viewCount: 245,
      replyCount: 4,
      isPinned: true,
      isLocked: false
    }
  ]
};

const MOCK_POSTS = {
  '101': [
    {
      id: '1001',
      content: 'Welcome to the official forums for ChoiceRealms! This is where you can discuss all aspects of the game, share your experiences, and connect with other players.\n\nPlease remember to follow our community guidelines and be respectful to other players.',
      author: 'GameMaster',
      createdAt: '2023-09-01T10:00:00Z',
      isEdited: false,
      reactions: {
        LIKE: 15,
        HELPFUL: 8,
        THANKS: 10
      }
    },
    {
      id: '1002',
      content: 'Thanks for creating this amazing game! I\'ve been enjoying it immensely so far.',
      author: 'NewPlayer',
      createdAt: '2023-09-01T10:30:00Z',
      isEdited: false,
      reactions: {
        LIKE: 5
      }
    },
    {
      id: '1003',
      content: 'I love the character creation system. So many options!',
      author: 'CharacterEnthusiast',
      createdAt: '2023-09-02T15:45:00Z',
      isEdited: true,
      reactions: {
        LIKE: 3,
        HELPFUL: 1
      }
    }
  ]
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getInitials = (name) => {
  return name.split(' ').map(part => part[0]).join('').toUpperCase();
};

const Tavern = () => {
  const [view, setView] = useState('categories');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPostContent, setNewPostContent] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkUser();
  }, []);

  async function checkUser() {
    try {
      const authenticatedUser = await getCurrentUser();
      setUser(authenticatedUser);
    } catch (err) {
      console.log('Error getting current user:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setView('threads');
  };

  const handleThreadSelect = (threadId) => {
    setSelectedThread(threadId);
    setView('thread');
  };

  const handleReaction = (postId, reactionType) => {
    console.log(`Reaction ${reactionType} to post ${postId}`);
    // This would be replaced with actual API call to add/remove reaction
  };

  const handleNewPost = (e) => {
    e.preventDefault();
    console.log('New post:', newPostContent);
    // This would be replaced with actual API call to create a new post
    setNewPostContent('');
    setShowPreview(false);
  };

  const handleNewThread = () => {
    // This would navigate to a new thread creation form
    navigate(`/tavern/category/${selectedCategory}/new-thread`);
  };

  const renderCategoriesList = () => (
    <div className="tavern-categories">
      <div className="category-list">
        {MOCK_CATEGORIES.map(category => (
          <div 
            key={category.id} 
            className="category-card"
            onClick={() => handleCategorySelect(category.id)}
          >
            <div className="category-card__header">
              <h2 className="category-card__title">{category.name}</h2>
              <div className="category-card__stats">
                <div className="category-card__stat">
                  <MessageSquare size={16} />
                  {category.threadCount} Threads
                </div>
                <div className="category-card__stat">
                  <MessageCircle size={16} />
                  {category.postCount} Posts
                </div>
              </div>
            </div>
            <p className="category-card__description">{category.description}</p>
            <div className="category-card__recent">
              <div className="category-card__recent-title">Recent Threads</div>
              {category.recentThreads.map(thread => (
                <div key={thread.id} className="recent-thread">
                  <Link 
                    to={`/tavern/thread/${thread.id}`} 
                    className="recent-thread__title"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleThreadSelect(thread.id);
                    }}
                  >
                    {thread.title}
                  </Link>
                  <div className="recent-thread__meta">
                    <span>{formatDate(thread.lastActivity)}</span>
                    <span>by {thread.author}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderThreadsList = () => {
    const category = MOCK_CATEGORIES.find(c => c.id === selectedCategory);
    const threads = MOCK_THREADS[selectedCategory] || [];

    return (
      <>
        <div className="thread-list-header">
          <h2 className="thread-list-title">{category?.name || 'Threads'}</h2>
          <button className="tavern-action-button" onClick={handleNewThread}>
            <Plus size={16} />
            New Thread
          </button>
        </div>
        <div className="thread-list">
          {threads.map(thread => (
            <div 
              key={thread.id} 
              className={`thread-card ${thread.isPinned ? 'thread-card--pinned' : ''} ${thread.isLocked ? 'thread-card--locked' : ''}`}
              onClick={() => handleThreadSelect(thread.id)}
            >
              <div className="thread-card__main">
                <Link 
                  to={`/tavern/thread/${thread.id}`} 
                  className="thread-card__title"
                  onClick={(e) => e.stopPropagation()}
                >
                  {thread.title}
                </Link>
                <div className="thread-card__meta">
                  <div className="thread-card__author">
                    <User size={14} />
                    {thread.author}
                  </div>
                  <div>
                    <Calendar size={14} />
                    {formatDate(thread.createdAt)}
                  </div>
                </div>
                <p className="thread-card__excerpt">{thread.content}</p>
              </div>
              <div className="thread-card__stats">
                <div className="thread-card__stat">
                  <div className="thread-card__stat-value">{thread.viewCount}</div>
                  <div className="thread-card__stat-label">Views</div>
                </div>
                <div className="thread-card__stat">
                  <div className="thread-card__stat-value">{thread.replyCount}</div>
                  <div className="thread-card__stat-label">Replies</div>
                </div>
              </div>
              <div className="thread-card__last-activity">
                <div className="thread-card__time">{formatDate(thread.lastActivity)}</div>
                <div className="thread-card__user">by {thread.lastPostAuthor}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="tavern-actions" style={{ marginTop: '2rem', justifyContent: 'space-between' }}>
          <button 
            className="tavern-action-button"
            onClick={() => setView('categories')}
          >
            Back to Categories
          </button>
          <button 
            className="tavern-action-button"
            onClick={handleNewThread}
          >
            <Plus size={16} />
            New Thread
          </button>
        </div>
      </>
    );
  };

  const renderThreadDetail = () => {
    // Find the current thread and its posts
    let currentThread = null;
    const categories = MOCK_CATEGORIES.filter(c => MOCK_THREADS[c.id]);
    
    for (const category of categories) {
      const thread = (MOCK_THREADS[category.id] || []).find(t => t.id === selectedThread);
      if (thread) {
        currentThread = thread;
        break;
      }
    }

    if (!currentThread) {
      return <div>Thread not found</div>;
    }

    const posts = MOCK_POSTS[currentThread.id] || [];

    return (
      <>
        <div className="thread-detail">
          <div className="thread-detail__header">
            <h1 className="thread-detail__title">{currentThread.title}</h1>
            <div className="thread-detail__meta">
              <div className="thread-detail__author">
                <div className="thread-detail__author-avatar">
                  {getInitials(currentThread.author)}
                </div>
                {currentThread.author}
              </div>
              <div>{formatDate(currentThread.createdAt)}</div>
            </div>
          </div>
        </div>
        
        <div className="post-list">
          <div className="post-list__header">
            <h2 className="post-list__title">Responses</h2>
          </div>
          
          {posts.map((post, index) => (
            <div key={post.id} id={`post-${post.id}`} className="post-item">
              <div className="post-item__header">
                <div className="post-item__author">
                  <div className="post-item__author-avatar">
                    {getInitials(post.author)}
                  </div>
                  <div>
                    <div className="post-item__author-name">{post.author}</div>
                    <div className="post-item__meta">
                      {index === 0 ? 'Original Poster' : `Reply #${index}`}
                    </div>
                  </div>
                </div>
                <div className="post-item__date">
                  <Clock size={14} />
                  {formatDate(post.createdAt)}
                  {post.isEdited && <span className="post-item__edited">(edited)</span>}
                </div>
              </div>
              <div className="post-item__content">{post.content}</div>
              <div className="post-item__actions">
                <div className="post-item__reactions">
                  {Object.entries(post.reactions || {}).map(([type, count]) => (
                    <button 
                      key={type} 
                      className="post-reaction"
                      onClick={() => handleReaction(post.id, type)}
                    >
                      {type === 'LIKE' && <ThumbsUp size={14} />}
                      {type === 'HELPFUL' && <Award size={14} />}
                      {type === 'THANKS' && '🙏'}
                      <span className="post-reaction__count">{count}</span>
                    </button>
                  ))}
                </div>
                <div className="post-item__buttons">
                  <button className="post-button">
                    <Reply size={14} />
                    Reply
                  </button>
                  {user && user.username === post.author && (
                    <>
                      <button className="post-button">
                        <Edit size={14} />
                        Edit
                      </button>
                      <button className="post-button">
                        <Trash size={14} />
                        Delete
                      </button>
                    </>
                  )}
                  <button className="post-button">
                    <Flag size={14} />
                    Report
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {!currentThread.isLocked && user && (
          <form className="post-reply-form" onSubmit={handleNewPost}>
            <div className="post-reply-form__header">
              <h2 className="post-reply-form__title">Post a Reply</h2>
            </div>
            {showPreview && (
              <div className="post-reply-form__preview">
                <h3 className="post-reply-form__preview-title">Preview</h3>
                <div className="post-reply-form__preview-content">
                  {newPostContent}
                </div>
              </div>
            )}
            <textarea 
              className="post-reply-form__content"
              value={newPostContent}
              onChange={(e) => setNewPostContent(e.target.value)}
              placeholder="Write your reply..."
              required
            />
            <div className="post-reply-form__actions">
              <button 
                type="button" 
                className="tavern-action-button"
                onClick={() => setShowPreview(!showPreview)}
              >
                {showPreview ? 'Hide Preview' : 'Preview'}
              </button>
              <button type="submit" className="tavern-action-button">
                Post Reply
              </button>
            </div>
          </form>
        )}
        
        <div className="tavern-actions" style={{ marginTop: '2rem' }}>
          <button 
            className="tavern-action-button"
            onClick={() => {
              setView('threads');
              setSelectedThread(null);
            }}
          >
            Back to Thread List
          </button>
        </div>
        
        <div className="pagination">
          <a href="#" className="pagination__item pagination__item--prev">«</a>
          <a href="#" className="pagination__item pagination__item--active">1</a>
          <a href="#" className="pagination__item">2</a>
          <a href="#" className="pagination__item">3</a>
          <a href="#" className="pagination__item pagination__item--next">»</a>
        </div>
      </>
    );
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="tavern-container">
      <header className="tavern-header">
        <div className="tavern-header__content">
          <h1 className="tavern-title">The Tavern</h1>
          <p className="tavern-description">
            A gathering place for adventurers from across the realms. Share your stories, 
            seek advice, and forge new alliances in this cozy tavern.
          </p>
          {view === 'categories' && (
            <div className="tavern-actions">
              <button className="tavern-action-button">
                <MessageSquare size={18} />
                Latest Posts
              </button>
              <button className="tavern-action-button">
                <Users size={18} />
                Active Users
              </button>
              <button className="tavern-action-button">
                <Eye size={18} />
                Mark All Read
              </button>
            </div>
          )}
        </div>
      </header>

      <main>
        {view === 'categories' && renderCategoriesList()}
        {view === 'threads' && renderThreadsList()}
        {view === 'thread' && renderThreadDetail()}
      </main>
    </div>
  );
};

export default Tavern;